import type { Project } from '@/domain/types';
import { projectEndTicks, routingOrder, tempoMapOf } from '@/domain/selectors';
import { ticksToSeconds } from '@/timing/tempoMap';
import type { AssetManager } from '@/assets/AssetManager';
import { TrackGraph } from './TrackGraph';
import { transportTells } from './wamTransport';
import { compensationFor } from './latency';
import { fadeGainAt, planWindow } from './schedulePlan';
import { applyVoiceEnvelope } from './voiceEnvelope';
import { activeLanes, automatedTrackParams, resolveParamDescriptor, writeLaneAutomation } from './automation';
import { planMidiWindow } from './midiPlan';
import { readSamplerZones } from './instruments/samplerState';
import { feedersOf } from '@/domain/freeze';
import { StretchCache, requiredRenders, toAudioBuffer } from './stretchCache';
import { secondsToTicks } from '@/timing/tempoMap';

/**
 * Offline rendering.
 *
 * Export runs the *same* project through the *same* graph construction and the
 * *same* scheduling planner as live playback, inside an `OfflineAudioContext`.
 * That is the whole point: an exported file that was produced by a separate
 * "render path" is a file that can silently disagree with what the user heard.
 * Here, the only differences from playback are that time runs as fast as the
 * CPU allows and that the destination is a buffer rather than a sound card.
 */

export interface RenderOptions {
  /**
   * Renders one track alone, tapped before its fader.
   *
   * This is what freezing needs: the clips, the instrument and the inserts, but
   * not the volume, pan or master processing that must stay live afterwards.
   * The rest of the project is not built at all, so freezing a track in a large
   * project costs only that track.
   */
  isolateTrackId?: string | null;
  /**
   * With `isolateTrackId`, taps the track before its inserts as well as before
   * its fader: the clips and nothing else. This is what consolidating needs —
   * the render goes back onto the same track, where the inserts, fader and
   * automation are still live and would otherwise be applied a second time.
   */
  clipsOnly?: boolean;
  /**
   * Renders these tracks as they sound in the mix — their clips, instruments,
   * inserts, fader and pan — but without the master's own processing.
   *
   * This is what bouncing needs, and it differs from both of the other modes.
   * `isolateTrackId` is pre-fader, because a freeze is replayed *through* the
   * fader afterwards. `soloTrackIds` renders the whole mix with everything else
   * silent, so the master chain is applied — which would be baked into a bounce
   * and then applied again on playback.
   */
  bounceTrackIds?: string[] | null;
  /**
   * Channel count of the render. Defaults to stereo; freezing passes the
   * track's own width so the pan law does not change underneath it.
   */
  channelCount?: number;
  startTicks?: number;
  endTicks?: number;
  sampleRate?: number;
  /** Extra seconds rendered past the end so reverb and delay tails are captured. */
  tailSec?: number;
  /**
   * Cache of pitch-preserved stretches. Passing the live engine's avoids
   * re-stretching audio that is already rendered for playback.
   */
  stretchCache?: StretchCache;
  /** Renders only these tracks (their buses and the master still apply). */
  soloTrackIds?: string[] | null;
  signal?: AbortSignal;
}

export class RenderError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RenderError';
  }
}

export async function renderProject(
  project: Project,
  assets: AssetManager,
  options: RenderOptions = {},
): Promise<AudioBuffer> {
  const map = tempoMapOf(project);
  const startTicks = options.startTicks ?? 0;
  const endTicks = options.endTicks ?? projectEndTicks(project);
  const startSec = ticksToSeconds(map, startTicks);
  const endSec = ticksToSeconds(map, Math.max(endTicks, startTicks));
  const tail = options.tailSec ?? 2;
  const durationSec = Math.max(0.01, endSec - startSec + tail);

  const sampleRate = options.sampleRate ?? 48000;
  const frames = Math.ceil(durationSec * sampleRate);

  const Ctor = (globalThis as { OfflineAudioContext?: typeof OfflineAudioContext }).OfflineAudioContext;
  if (!Ctor) throw new RenderError('This browser does not support offline rendering, so export is unavailable.');
  if (frames > sampleRate * 60 * 60 * 3) {
    throw new RenderError('This project is longer than the three-hour export limit.');
  }

  const ctx = new Ctor(Math.min(2, Math.max(1, options.channelCount ?? 2)), frames, sampleRate);

  // Decode everything the render needs up front. Unlike live playback, an
  // offline render cannot "catch it next time round" — a buffer that is not
  // resident when its voice is created is simply missing from the file.
  const neededAssets = new Set<string>();
  for (const clip of Object.values(project.clips)) {
    if (clip.kind === 'audio') neededAssets.add(clip.assetId);
  }
  // Sampler zones reference audio too, and an unloaded zone would render as
  // silence with nothing to explain it.
  for (const track of Object.values(project.tracks)) {
    if (track.freeze) neededAssets.add(track.freeze.assetId);
    for (const effect of track.effects) if (effect.impulseAssetId) neededAssets.add(effect.impulseAssetId);
    if (track.instrument?.type !== 'sampler') continue;
    for (const zone of readSamplerZones(track.instrument.data)) neededAssets.add(zone.assetId);
  }
  const buffers = new Map<string, AudioBuffer>();
  for (const assetId of neededAssets) {
    if (options.signal?.aborted) throw new RenderError('Export cancelled.');
    const buffer = await assets.getBuffer(assetId);
    if (buffer) buffers.set(assetId, buffer);
  }

  // Stretched, reversed and transposed clips read a rendered buffer rather than
  // their asset, so those renders have to exist before the graph is built, for
  // the same reason the assets do. A shared cache is passed in when one is available — the live
  // engine's — so exporting does not repeat work playback has already done.
  const stretches =
    options.stretchCache ?? new StretchCache((assetId) => assets.getBuffer(assetId), assets.storage);
  for (const render of requiredRenders(project)) {
    if (options.signal?.aborted) throw new RenderError('Export cancelled.');
    if (render.kind === 'warp') {
      const warped = await stretches.ensureWarped(render);
      if (warped) buffers.set(render.key, toAudioBuffer(ctx, warped));
      continue;
    }
    const audio =
      render.kind === 'ratio'
        ? await stretches.ensure(
            render.assetId,
            render.ratio,
            render.reversed,
            render.semitones,
            render.formantSemitones,
          )
        : render.kind === 'source'
          ? await stretches.ensureSource(
              render.assetId,
              render.reversed,
              render.semitones,
              render.formantSemitones,
            )
          : null;
    if (audio) buffers.set(render.key, toAudioBuffer(ctx, audio));
  }

  // Build the graph exactly as the live engine does.
  const isolate = options.isolateTrackId ?? null;
  const bounce = options.bounceTrackIds ?? null;
  const rendered = isolate
    ? isolateTrack(project, isolate, options.clipsOnly ?? false)
    : bounce
      ? isolateForBounce(project, bounce)
      : applySoloMask(project, options.soloTrackIds ?? null);
  const graphs = new Map<string, TrackGraph>();
  const resolveBuffer = (assetId: string): AudioBuffer | null => buffers.get(assetId) ?? null;
  for (const trackId of Object.keys(rendered.tracks)) {
    graphs.set(trackId, new TrackGraph(ctx, trackId, resolveBuffer));
  }

  for (const trackId of routingOrder(rendered)) {
    const track = rendered.tracks[trackId];
    const graph = graphs.get(trackId);
    if (!track || !graph) continue;

    if (isolate) {
      if (trackId === isolate) {
        // The tap is the send point: everything up to the fader, and nothing
        // after it.
        graph.sendSource(true).connect(ctx.destination);
        graph.disconnectOutput();
      } else {
        // Anything else in an isolated render is there because it *feeds* the
        // isolated track — freezing a bus renders the tracks routed into it — so
        // it stays wired to whatever it feeds. Disconnecting these as well, which
        // is what this did when only a single track could be isolated, left the
        // bus with nothing arriving and rendered silence.
        const target = graphs.get(track.outputTrackId ?? '');
        if (target) graph.connectOutput(target.input);
        else graph.disconnectOutput();
      }
    } else {
      graph.connectOutput(
        track.kind === 'master' ? ctx.destination : (graphs.get(track.outputTrackId ?? '')?.input ?? ctx.destination),
      );
    }
    graph.update(rendered, track, automatedTrackParams(rendered, trackId));
    for (const send of track.sends) {
      const target = graphs.get(send.targetTrackId);
      if (!target) continue;
      const { gain, out } = graph.ensureSend(send.id, send.preFader);
      out.connect(target.input);
      gain.gain.value = send.enabled ? send.gain : 0;
    }
  }

  // An insert built in a worklet is not in the chain until its module has
  // loaded, and has no parameter to automate until then either. Nothing is
  // scheduled, and nothing rendered, before every chain is whole.
  await Promise.all([...graphs.values()].map((graph) => graph.whenReady() ?? Promise.resolve()));

  // Every plugin is in and has said how late it answers: line the paths up,
  // exactly as playback does once its plugins have landed.
  const compensation = compensationFor(rendered, (trackId) => graphs.get(trackId)?.chainLatencySec() ?? 0);
  for (const [trackId, graph] of graphs) {
    graph.setCompensation(
      compensation.common.get(trackId) ?? 0,
      compensation.output.get(trackId) ?? 0,
      compensation.sends.get(trackId) ?? new Map(),
    );
  }

  // Automation is written across the whole render before any voice starts, from
  // the same lane data and through the same writer live playback uses. An
  // export that ignored automation, or applied it through a different path,
  // would not be the mix the user heard.
  const renderTimeFor = (seconds: number): number => seconds - startSec;

  // Hosted plugins are told the transport across the render, on the render's
  // clock, exactly as playback tells them on the live one; a sequencer in a
  // plugin renders what it played.
  const tells = transportTells(rendered, startSec, endSec + tail, (seconds) => renderTimeFor(seconds));
  for (const graph of graphs.values()) graph.tellTransport(tells);
  for (const lane of activeLanes(rendered)) {
    const descriptor = resolveParamDescriptor(rendered, lane.target);
    const graph = graphs.get(lane.target.trackId);
    if (!descriptor || !graph) continue;
    writeLaneAutomation(
      graph.automationTargets(lane.target),
      lane,
      descriptor,
      secondsToTicks(map, startSec),
      secondsToTicks(map, endSec + tail),
      map,
      renderTimeFor,
    );
  }

  // Plan the entire span as a single window with no looping.
  const bounds = {
    segmentStartSec: startSec,
    segmentEndSec: endSec,
    windowStartSec: startSec - 1e-9,
    windowEndSec: endSec,
  };
  const plans = planWindow(rendered, bounds);

  // MIDI goes through the same planner and the same instruments as playback.
  for (const note of planMidiWindow(rendered, bounds)) {
    const instrument = graphs.get(note.trackId)?.instrumentNode;
    if (!instrument) continue;
    instrument.playNote(note.pitch, note.velocity, note.startProjectSec - startSec, note.durationSec);
  }

  for (const plan of plans) {
    const buffer = buffers.get(plan.bufferKey);
    const graph = graphs.get(plan.trackId);
    if (!buffer || !graph) continue;

    const when = plan.startProjectSec - startSec;
    const gain = ctx.createGain();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = plan.playbackRate;

    // A node of its own for the envelope, ahead of the fades: they are written
    // with `setValueCurveAtTime`, which owns the parameter for its duration.
    if (plan.envelope.length > 0) {
      const envelope = ctx.createGain();
      source.connect(envelope);
      envelope.connect(gain);
      applyVoiceEnvelope(envelope.gain, when, plan.envelope);
    } else {
      source.connect(gain);
    }
    gain.connect(plan.destination === 'preFader' ? graph.frozenInput : graph.input);

    gain.gain.setValueAtTime(plan.gain * (plan.fadeIn ? plan.fadeIn.startGain : 1), when);
    if (plan.fadeIn) writeFade(gain, when, plan.gain, plan.fadeIn, true);
    if (plan.fadeOut) writeFade(gain, when, plan.gain, plan.fadeOut, false);

    source.start(when, plan.sourceOffsetSec, plan.durationSec * plan.playbackRate);
  }

  const result = await ctx.startRendering();
  for (const graph of graphs.values()) graph.dispose();
  return result;
}

function writeFade(
  gain: GainNode,
  voiceStart: number,
  base: number,
  fade: {
    startOffsetSec: number;
    durationSec: number;
    curve: 'linear' | 'equalPower' | 'exponential';
    curvature: number;
    startGain: number;
    endGain: number;
  },
  rising: boolean,
): void {
  if (fade.durationSec <= 0) {
    gain.gain.setValueAtTime(base * fade.endGain, voiceStart + fade.startOffsetSec);
    return;
  }
  const points = 64;
  const curve = new Float32Array(points);
  const span = rising ? 1 - fade.startGain : fade.startGain;
  for (let i = 0; i < points; i++) {
    const shaped = fadeGainAt(fade.curve, (i / (points - 1)) * fade.durationSec, fade.durationSec, rising, fade.curvature);
    const value = rising ? fade.startGain + shaped * span : shaped * span;
    curve[i] = base * Math.min(1, Math.max(0, value));
  }
  gain.gain.setValueCurveAtTime(curve, voiceStart + fade.startOffsetSec, fade.durationSec);
}

/**
 * Keeps one track and drops the rest.
 *
 * Solo and mute are cleared on it as well: a frozen render is the track's own
 * material, and baking in a mute would produce a silent file that looks like a
 * bug when the mute is later lifted.
 */
/**
 * The project as it would sound if only these tracks played, with the master
 * passing everything straight through.
 *
 * The tracks keep their own faders, pans, inserts and sends, because that is
 * what "how this part sounds" means. The buses they feed are kept too — a part
 * routed through a drum bus with a compressor on it does not sound like the same
 * part without one. What is dropped is the master's processing and level, since
 * a bounce is played back *through* the master and baking it in would apply it
 * twice.
 */
function isolateForBounce(project: Project, trackIds: readonly string[]): Project {
  const wanted = new Set(trackIds);

  // Follow each track's output chain so the buses it passes through come too.
  for (const trackId of trackIds) {
    let current = project.tracks[trackId]?.outputTrackId ?? null;
    for (let guard = 0; guard < 32 && current; guard++) {
      const next = project.tracks[current];
      if (!next || next.kind === 'master') break;
      wanted.add(current);
      current = next.outputTrackId;
    }
  }

  const tracks: Project['tracks'] = {};
  const clips: Project['clips'] = {};
  for (const [trackId, track] of Object.entries(project.tracks)) {
    if (track.kind === 'master') {
      // A master that only sums: no inserts, no fader move, nothing to bake in.
      tracks[trackId] = { ...track, effects: [], volume: 1, pan: 0, mute: false, solo: false, sends: [] };
      continue;
    }
    if (!wanted.has(trackId)) continue;
    tracks[trackId] = { ...track, mute: false, solo: false };
    for (const clipId of track.clipIds) {
      const clip = project.clips[clipId];
      if (clip) clips[clipId] = clip;
    }
  }

  const trackOrder = project.trackOrder.filter((id) => tracks[id]);
  return { ...project, tracks, trackOrder, clips };
}

function isolateTrack(project: Project, trackId: string, clipsOnly = false): Project {
  const track = project.tracks[trackId];
  if (!track) return project;

  // A bus has no clips: what it plays is whatever routes into it, so freezing
  // one has to render those tracks too, through the bus's own inserts. They keep
  // their faders and pans, because that is their contribution to the bus.
  const feeders = feedersOf(project, trackId);

  const tracks: Project['tracks'] = {
    [trackId]: {
      ...track,
      mute: false,
      solo: false,
      freeze: null,
      outputTrackId: null,
      sends: [],
      // Clips only: no inserts, so the pre-fader tap is the sum of the voices.
      effects: clipsOnly ? [] : track.effects,
    },
  };
  const clips: Project['clips'] = {};

  for (const id of [trackId, ...feeders]) {
    const source = project.tracks[id];
    if (!source) continue;
    if (id !== trackId) {
      tracks[id] = { ...source, mute: false, solo: false, sends: [] };
    }
    for (const clipId of source.clipIds) {
      const clip = project.clips[clipId];
      if (clip) clips[clipId] = clip;
    }
  }

  return {
    ...project,
    // The frozen track routes to nowhere: it is tapped directly, and leaving it
    // pointed at a master that is no longer in the project would make it read as
    // inaudible and render silence.
    tracks,
    trackOrder: project.trackOrder.filter((id) => tracks[id]),
    clips,
  };
}

/**
 * Stem rendering mutes everything except the requested tracks, leaving their
 * routing intact — so a stem taken from a track that feeds a bus still carries
 * that bus's processing, which is what "stem" means to anyone who will open
 * these files later.
 */
function applySoloMask(project: Project, soloTrackIds: string[] | null): Project {
  if (!soloTrackIds) return project;
  const keep = new Set(soloTrackIds);
  const tracks = { ...project.tracks };
  for (const [id, track] of Object.entries(tracks)) {
    if (track.kind === 'master' || track.kind === 'bus' || track.kind === 'aux') continue;
    tracks[id] = { ...track, mute: !keep.has(id), solo: false };
  }
  return { ...project, tracks };
}

/** Converts a rendered AudioBuffer into plain channel arrays for encoding. */
export function bufferToChannels(buffer: AudioBuffer): Float32Array[] {
  const channels: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = new Float32Array(buffer.length);
    buffer.copyFromChannel(data, c);
    channels.push(data);
  }
  return channels;
}
